/**
 * Column config + dummy fallback cho dashboard "Futures Position"
 * (`/dashboard/futures-position`) — convert từ PBI page "Futures Postion". 4 bảng:
 *   1. London - Robusta   (bảng giá futures, market_price LRC)
 *   2. Newyork - Arabica   (bảng giá futures, market_price KCE)
 *   3. Futures Position   (full_symbol + bought/sold/PTBF/open + Total)
 *   4. Total lot          (ma trận flag × BUY/SELL + Total — CỘT ĐỘNG theo API)
 *
 * Data thật từ `useGetFuturesPositionDashboardQuery` (Report DB); rows dưới đây
 * là DUMMY fallback hiển thị trong lúc load. Key camelCase khớp BE
 * (`dashboard_futures_position_pg.py`).
 */
import type { PnLColumn, PnLHeaderGroup, PnLRow } from '../pnl/PnLTable'

import type { TotalLotRow } from '../../../store/api/dashboardFuturesPositionApi'

export interface FpTable {
  columns: PnLColumn[]
  rows: PnLRow[]
  headerGroups?: PnLHeaderGroup[]
}

// ── 1 & 2. Bảng giá futures (London / Newyork) — cùng cấu trúc cột ──
// Format Number: giá 2 decimals (Standard, căn phải); volume/oi/mo nguyên (0 dp,
// thousand sep); số âm theo chuẩn dashboard = ngoặc + đỏ (KHÔNG signed — bảng
// Newyork toàn change âm, chị Đẹp yêu cầu 2026-06-10). Format Date: timing →
// DD-MMM-YYYY qua `fmtDate` (date: true).
// Label tidy khỏi tên cột raw PBI: hoa chữ cái đầu + bỏ `_` (chị Đẹp 2026-06-10);
// `OI` = Open Interest giữ dạng acronym.
const QUOTE_COLUMNS: PnLColumn[] = [
  { key: 'contract', label: 'Contract', text: true },
  { key: 'last', label: 'Last', decimals: 2 },
  { key: 'change', label: 'Change', decimals: 2 },
  { key: 'high', label: 'High', decimals: 2 },
  { key: 'low', label: 'Low', decimals: 2 },
  { key: 'prevOpen', label: 'Prev open', decimals: 2 },
  { key: 'volume', label: 'Volume', decimals: 0 },
  { key: 'oi', label: 'OI', decimals: 0 },
  { key: 'timing', label: 'Timing', text: true, date: true },
  { key: 'mo', label: 'Mo', decimals: 0 },
]

export const londonRobusta: FpTable = {
  columns: QUOTE_COLUMNS,
  rows: [
    { contract: 'N26', last: 3333, change: 17, high: 3374, low: 3302, prevOpen: 3317, volume: 7735, oi: 30403, timing: '2026-06-08', mo: 1 },
    { contract: 'U26', last: 3260, change: 27, high: 3288, low: 3217, prevOpen: 3234, volume: 11992, oi: 40905, timing: '2026-06-08', mo: 2 },
    { contract: 'X26', last: 3189, change: 25, high: 3215, low: 3149, prevOpen: 3179, volume: 2481, oi: 13036, timing: '2026-06-08', mo: 3 },
    { contract: 'F27', last: 3123, change: 25, high: 3140, low: 3082, prevOpen: 3113, volume: 844, oi: 6967, timing: '2026-06-08', mo: 4 },
    { contract: 'H27', last: 3087, change: 30, high: 3098, low: 3040, prevOpen: 3049, volume: 403, oi: 2622, timing: '2026-06-08', mo: 5 },
    { contract: 'K27', last: 3063, change: 32, high: 3063, low: 3049, prevOpen: 3049, volume: 122, oi: 1210, timing: '2026-06-08', mo: 6 },
    { contract: 'N27', last: 3044, change: 32, high: 3044, low: 3029, prevOpen: 3029, volume: 72, oi: 569, timing: '2026-06-08', mo: 7 },
    { contract: 'U27', last: 3025, change: 30, high: 3025, low: 3011, prevOpen: 3011, volume: 66, oi: 333, timing: '2026-06-08', mo: 8 },
  ],
}

export const newyorkArabica: FpTable = {
  columns: QUOTE_COLUMNS,
  rows: [
    { contract: 'N26', last: 245.9, change: -0.6, high: 250.85, low: 244.3, prevOpen: 246, volume: 22261, oi: 63436, timing: '2026-06-08', mo: 1 },
    { contract: 'U26', last: 241.65, change: -0.45, high: 246.1, low: 240, prevOpen: 242, volume: 19982, oi: 73584, timing: '2026-06-08', mo: 2 },
    { contract: 'Z26', last: 234.35, change: -0.5, high: 238.45, low: 232.45, prevOpen: 234.75, volume: 8142, oi: 45509, timing: '2026-06-08', mo: 3 },
    { contract: 'H27', last: 232.1, change: -0.65, high: 236, low: 231, prevOpen: 232.95, volume: 3624, oi: 15888, timing: '2026-06-08', mo: 4 },
    { contract: 'K27', last: 232.2, change: -0.5, high: 235.6, low: 231.6, prevOpen: 232.9, volume: 1362, oi: 6261, timing: '2026-06-08', mo: 5 },
    { contract: 'N27', last: 232.8, change: -0.25, high: 235.75, low: 232, prevOpen: 232, volume: 426, oi: 4079, timing: '2026-06-08', mo: 6 },
    { contract: 'U27', last: 232.5, change: -0.4, high: 235.25, low: 232.25, prevOpen: 232.65, volume: 86, oi: 1901, timing: '2026-06-08', mo: 7 },
    { contract: 'Z27', last: 231.95, change: -0.6, high: 234.35, low: 231.7, prevOpen: 232.6, volume: 109, oi: 2590, timing: '2026-06-08', mo: 8 },
  ],
}

// ── 3. Futures Position ──
export const futuresPosition: FpTable = {
  columns: [
    { key: 'fullSymbol', label: 'Full symbol', text: true },
    { key: 'totalBought', label: 'Total bought', decimals: 0 },
    { key: 'totalSold', label: 'Total sold', decimals: 0 },
    { key: 'ptbfPurchase', label: 'PTBF Purchase', decimals: 0 },
    { key: 'ptbfSale', label: 'PTBF Sale', decimals: 0 },
    { key: 'openPosition', label: 'Open position', decimals: 0 },
  ],
  rows: [
    { fullSymbol: 'LRCU21', totalBought: 349, totalSold: -349, ptbfPurchase: null, ptbfSale: 0, openPosition: 0 },
    { fullSymbol: 'LRCX21', totalBought: 326, totalSold: -326, ptbfPurchase: 0, ptbfSale: null, openPosition: 0 },
    { fullSymbol: 'LRCF22', totalBought: 628, totalSold: -628, ptbfPurchase: 0, ptbfSale: 0, openPosition: 0 },
    { fullSymbol: 'LRCH22', totalBought: 1374, totalSold: -1374, ptbfPurchase: 0, ptbfSale: 0, openPosition: 0 },
    { fullSymbol: 'LRCK22', totalBought: 2762, totalSold: -3187, ptbfPurchase: 0, ptbfSale: 0, openPosition: -425 },
    { fullSymbol: 'LRCN22', totalBought: 3361, totalSold: -3028, ptbfPurchase: 0, ptbfSale: 0, openPosition: 333 },
    { fullSymbol: 'Total', totalBought: 73089, totalSold: -73895, ptbfPurchase: -343, ptbfSale: 1217, openPosition: 68, isTotal: true },
  ],
}

// ── 4. Total lot — ma trận flag × (BUY, SELL), cột ĐỘNG theo flags từ API ──
/** Key cột flat cho 1 ô (flag, side) — dùng chung khi build columns & flatten rows. */
export const tlKey = (flag: string, side: 'buy' | 'sell') =>
  `${flag.toLowerCase().replace(/[^a-z0-9]+/g, '_')}_${side}`

/** Dựng config bảng Total lot từ danh sách flag (API `totalLot.flags`). */
export function buildTotalLotTable(flags: string[], rows: PnLRow[]): FpTable {
  return {
    headerGroups: [
      { label: 'Flag', span: 1 },
      ...flags.map((f) => ({ label: f, span: 2 })),
    ],
    columns: [
      { key: 'fullSymbol', label: 'Full symbol', text: true },
      ...flags.flatMap((f) => [
        { key: tlKey(f, 'buy'), label: 'BUY', decimals: 0 } as PnLColumn,
        { key: tlKey(f, 'sell'), label: 'SELL', decimals: 0 } as PnLColumn,
      ]),
    ],
    rows,
  }
}

/** Flatten rows API (`values[flag] = {buy, sell}`) → row phẳng theo `tlKey`. */
export function flattenTotalLotRows(rows: TotalLotRow[]): PnLRow[] {
  return rows.map((r) => {
    const flat: PnLRow = { fullSymbol: r.fullSymbol }
    if (r.isTotal) flat.isTotal = true
    for (const [flag, cell] of Object.entries(r.values)) {
      flat[tlKey(flag, 'buy')] = cell.buy
      flat[tlKey(flag, 'sell')] = cell.sell
    }
    return flat
  })
}

const MOCK_FLAGS = ['Arb-Loic', 'Daniel prop', 'Futures', 'Hedge']

export const totalLot: FpTable = buildTotalLotTable(MOCK_FLAGS, [
  { fullSymbol: 'LRCU21', [tlKey('Hedge', 'buy')]: 349, [tlKey('Hedge', 'sell')]: -349 },
  { fullSymbol: 'LRCX21', [tlKey('Futures', 'buy')]: 100, [tlKey('Futures', 'sell')]: -100, [tlKey('Hedge', 'buy')]: 326, [tlKey('Hedge', 'sell')]: -326 },
  { fullSymbol: 'LRCF22', [tlKey('Futures', 'buy')]: 251, [tlKey('Futures', 'sell')]: -251, [tlKey('Hedge', 'buy')]: 628, [tlKey('Hedge', 'sell')]: -628 },
  { fullSymbol: 'LRCK22', [tlKey('Arb-Loic', 'buy')]: 457, [tlKey('Arb-Loic', 'sell')]: -465, [tlKey('Daniel prop', 'buy')]: 675, [tlKey('Daniel prop', 'sell')]: -157, [tlKey('Futures', 'buy')]: 2762, [tlKey('Futures', 'sell')]: -3166, [tlKey('Hedge', 'buy')]: 1669, [tlKey('Hedge', 'sell')]: -1754 },
  {
    fullSymbol: 'Total',
    [tlKey('Arb-Loic', 'buy')]: 53,
    [tlKey('Arb-Loic', 'sell')]: -53,
    [tlKey('Daniel prop', 'buy')]: 1666,
    [tlKey('Daniel prop', 'sell')]: -1666,
    [tlKey('Futures', 'buy')]: 71022,
    [tlKey('Futures', 'sell')]: -71895,
    [tlKey('Hedge', 'buy')]: 30213,
    [tlKey('Hedge', 'sell')]: -30213,
    isTotal: true,
  },
])
